/**
 * Experiences Admin Page
 * 
 * Panel de administración para gestionar experiencias interactivas
 * (juegos, calculadoras, visualizaciones) que se embeben en artículos.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '@/components/Header';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'wouter';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  Play,
  Loader2,
  Save,
  X,
  Copy,
  Check,
  AlertCircle,
  Gamepad2,
  Calculator,
  BarChart3,
  HelpCircle,
  FlaskConical,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Experience {
  id: string;
  title: string;
  description?: string;
  htmlUrl: string;
  type: 'game' | 'calculator' | 'visualization' | 'quiz' | 'simulator' | 'other';
  height: number;
  thumbnailUrl?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface ExperienceFormData {
  title: string;
  description: string;
  sourceFile: string;  // Filename from experiences-source folder
  type: 'game' | 'calculator' | 'visualization' | 'quiz' | 'simulator' | 'other';
  height: number;
  thumbnailUrl: string;
}

interface SourceFile {
  filename: string;
  name: string;
}

const typeIcons: Record<string, React.ReactNode> = {
  game: <Gamepad2 className="w-5 h-5" />,
  calculator: <Calculator className="w-5 h-5" />,
  visualization: <BarChart3 className="w-5 h-5" />,
  quiz: <HelpCircle className="w-5 h-5" />,
  simulator: <FlaskConical className="w-5 h-5" />,
  other: <Sparkles className="w-5 h-5" />,
};

const typeLabels: Record<string, string> = {
  game: 'Juego',
  calculator: 'Calculadora',
  visualization: 'Visualización',
  quiz: 'Quiz',
  simulator: 'Simulador',
  other: 'Otro',
};

const typeColors: Record<string, string> = {
  game: 'bg-purple-100 text-purple-700 border-purple-200',
  calculator: 'bg-blue-100 text-blue-700 border-blue-200',
  visualization: 'bg-green-100 text-green-700 border-green-200',
  quiz: 'bg-orange-100 text-orange-700 border-orange-200',
  simulator: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  other: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function ExperiencesAdmin() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Calculate isAdmin from user role
  const isAdmin = user?.role === 'admin';

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [updateHtmlId, setUpdateHtmlId] = useState<string | null>(null);
  const [selectedSourceFile, setSelectedSourceFile] = useState<string>('');

  const [formData, setFormData] = useState<ExperienceFormData>({
    title: '',
    description: '',
    sourceFile: '',
    type: 'other',
    height: 500,
    thumbnailUrl: '',
  });

  // Redirect non-admins (only after auth is loaded)
  React.useEffect(() => {
    if (!authLoading && isAuthenticated && !isAdmin) {
      setLocation('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, setLocation]);

  // Fetch experiences
  const { data: experiencesData, isLoading: experiencesLoading } = useQuery<{ data: Experience[] }>({
    queryKey: ['admin-experiences'],
    queryFn: async () => {
      const response = await fetch('/api/experiences', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch experiences');
      return response.json();
    },
    enabled: isAdmin,
  });

  const experiences = experiencesData?.data || [];

  // Fetch available source files
  const { data: sourceFilesData } = useQuery<{ data: SourceFile[] }>({
    queryKey: ['experience-source-files'],
    queryFn: async () => {
      const response = await fetch('/api/experiences/source-files', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch source files');
      return response.json();
    },
    enabled: isAdmin && (isCreating || !!editingId || !!updateHtmlId),
  });

  const sourceFiles = sourceFilesData?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ExperienceFormData) => {
      const response = await fetch('/api/experiences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create experience');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      setIsCreating(false);
      resetForm();
      toast({ title: 'Experiencia creada', description: 'La experiencia se ha creado correctamente.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo crear la experiencia.', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ExperienceFormData> }) => {
      const response = await fetch(`/api/experiences/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update experience');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      setEditingId(null);
      resetForm();
      toast({ title: 'Experiencia actualizada', description: 'Los cambios se han guardado correctamente.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar la experiencia.', variant: 'destructive' });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await fetch(`/api/experiences/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive }),
      });
      if (!response.ok) throw new Error('Failed to toggle experience');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/experiences/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete experience');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      toast({ title: 'Experiencia eliminada', description: 'La experiencia se ha eliminado correctamente.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo eliminar la experiencia.', variant: 'destructive' });
    },
  });

  // Update HTML mutation
  const updateHtmlMutation = useMutation({
    mutationFn: async ({ id, sourceFile }: { id: string; sourceFile: string }) => {
      const response = await fetch(`/api/experiences/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sourceFile }),
      });
      if (!response.ok) throw new Error('Failed to update HTML');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      toast({ title: 'HTML actualizado', description: 'El contenido HTML se ha actualizado correctamente.' });
      setUpdateHtmlId(null);
      setSelectedSourceFile('');
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo actualizar el HTML.', variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      sourceFile: '',
      type: 'other',
      height: 500,
      thumbnailUrl: '',
    });
  };

  const handleEdit = (experience: Experience) => {
    setEditingId(experience.id);
    setFormData({
      title: experience.title,
      description: experience.description || '',
      sourceFile: '', // Will select new file if updating
      type: experience.type,
      height: experience.height,
      thumbnailUrl: experience.thumbnailUrl || '',
    });
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Acceso denegado</h1>
          <p className="text-gray-600 mt-2">No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Experiencias Interactivas</h1>
            <p className="text-gray-600 mt-1">
              Gestiona juegos, calculadoras y visualizaciones para embeber en artículos
            </p>
          </div>
          <button
            onClick={() => {
              setIsCreating(true);
              setEditingId(null);
              resetForm();
            }}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Nueva experiencia
          </button>
        </div>

        {/* Create/Edit Form */}
        {(isCreating || editingId) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Editar experiencia' : 'Nueva experiencia'}
              </h2>
              <button
                onClick={() => {
                  setIsCreating(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Calculadora de inflación"
                    required
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as ExperienceFormData['type'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(typeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="Calcula cómo afecta la inflación a tu poder adquisitivo..."
                />
              </div>

              {/* Source File Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Archivo HTML {!editingId && '*'}
                  <span className="text-gray-500 font-normal ml-2">
                    (de la carpeta experiences-source/)
                  </span>
                </label>
                <select
                  value={formData.sourceFile}
                  onChange={(e) => setFormData({ ...formData, sourceFile: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required={!editingId}
                >
                  <option value="">-- Selecciona un archivo --</option>
                  {sourceFiles.map((file) => (
                    <option key={file.filename} value={file.filename}>
                      {file.name} ({file.filename})
                    </option>
                  ))}
                </select>
                {sourceFiles.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    ⚠️ No hay archivos HTML en la carpeta experiences-source/
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Height */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Altura del iframe (px)
                  </label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) || 500 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min={200}
                    max={2000}
                  />
                </div>

                {/* Thumbnail URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de miniatura (opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.thumbnailUrl}
                    onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingId(null);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {editingId ? 'Guardar cambios' : 'Crear experiencia'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Experiences List */}
        {experiencesLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-600">Cargando experiencias...</span>
          </div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600">No hay experiencias</h3>
            <p className="text-gray-500 mt-2">Crea tu primera experiencia interactiva</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {experiences.map((experience) => (
              <div
                key={experience.id}
                className={`bg-white rounded-xl border p-6 transition-all ${
                  experience.isActive ? 'border-gray-200' : 'border-gray-300 bg-gray-50 opacity-75'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Type Badge */}
                    <div className={`p-3 rounded-xl border ${typeColors[experience.type]}`}>
                      {typeIcons[experience.type]}
                    </div>

                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-gray-900">{experience.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[experience.type]}`}>
                          {typeLabels[experience.type]}
                        </span>
                        {!experience.isActive && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                            Desactivada
                          </span>
                        )}
                      </div>
                      {experience.description && (
                        <p className="text-gray-600 mt-1 text-sm">{experience.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span>Altura: {experience.height}px</span>
                        <span>•</span>
                        <button
                          onClick={() => handleCopyId(experience.id)}
                          className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        >
                          {copiedId === experience.id ? (
                            <>
                              <Check className="w-4 h-4 text-green-500" />
                              <span className="text-green-600">Copiado</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span>ID: {experience.id.slice(0, 8)}...</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewId(previewId === experience.id ? null : experience.id)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                      title="Vista previa"
                    >
                      <Play className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setUpdateHtmlId(updateHtmlId === experience.id ? null : experience.id);
                        setSelectedSourceFile('');
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        updateHtmlId === experience.id ? 'bg-green-100 text-green-700' : 'hover:bg-green-50 text-green-600'
                      }`}
                      title="Actualizar HTML"
                    >
                      <RefreshCw className={`w-5 h-5 ${updateHtmlMutation.isPending && updateHtmlId === experience.id ? 'animate-spin' : ''}`} />
                    </button>
                    <a
                      href={experience.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
                      title="Abrir en nueva pestaña"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => toggleActiveMutation.mutate({ id: experience.id, isActive: !experience.isActive })}
                      className={`p-2 rounded-lg transition-colors ${
                        experience.isActive ? 'hover:bg-yellow-50 text-yellow-600' : 'hover:bg-green-50 text-green-600'
                      }`}
                      title={experience.isActive ? 'Desactivar' : 'Activar'}
                    >
                      {experience.isActive ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => handleEdit(experience)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                      title="Editar"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('¿Estás seguro de que quieres eliminar esta experiencia?')) {
                          deleteMutation.mutate(experience.id);
                        }
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
                      title="Eliminar"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Update HTML Panel */}
                {updateHtmlId === experience.id && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <RefreshCw className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Actualizar HTML</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={selectedSourceFile}
                        onChange={(e) => setSelectedSourceFile(e.target.value)}
                        className="flex-1 px-4 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      >
                        <option value="">Seleccionar archivo fuente...</option>
                        {sourceFiles.map((file) => (
                          <option key={file.filename} value={file.filename}>
                            {file.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          if (selectedSourceFile) {
                            updateHtmlMutation.mutate({ id: experience.id, sourceFile: selectedSourceFile });
                          }
                        }}
                        disabled={!selectedSourceFile || updateHtmlMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updateHtmlMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Actualizar
                      </button>
                      <button
                        onClick={() => {
                          setUpdateHtmlId(null);
                          setSelectedSourceFile('');
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      El ID de la experiencia se mantendrá igual. Solo se actualizará el contenido HTML.
                    </p>
                  </div>
                )}

                {/* Preview */}
                {previewId === experience.id && (
                  <div className="mt-6 border-t pt-6">
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      <iframe
                        src={`/api/experiences/${experience.id}/embed`}
                        title={experience.title}
                        width="100%"
                        height={experience.height}
                        className="border-0"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

